/**
 * Scoring & standings engine — pure calculations.
 *
 * The service layer is responsible for I/O (read/write the store, persist
 * GameResult & StandingsRecord). This module has no side effects: feed it
 * inputs, get standings/stats out. Easy to test, easy to call from anywhere.
 */

import type {
  Division,
  Game,
  GameResult,
  GoalieStat,
  Id,
  ScoreEvent,
  StandingsRecord,
  Team,
  TournamentSettings,
  GameRoster,
} from '../models';

export interface GameTally {
  homeScore: number;
  awayScore: number;
  homeShootoutWins: number;
  awayShootoutWins: number;
  homeShootoutGoals: number;
  awayShootoutGoals: number;
  shootoutAttempts: number;
  homeShots: number;
  awayShots: number;
  homePims: number;
  awayPims: number;
  /** Goals per playerId (across both teams). */
  goalsByPlayer: Record<Id, number>;
  assistsByPlayer: Record<Id, number>;
  shotsByPlayer: Record<Id, number>;
  pimsByPlayer: Record<Id, number>;
}

export function resolvePeriodLengthMinutes(
  scoring: Pick<TournamentSettings['scoring'], 'periodCount' | 'periodLengthMinutes' | 'gameLengthMinutes'>,
): number {
  const periodLength = Number(scoring.periodLengthMinutes);
  if (Number.isFinite(periodLength) && periodLength > 0) return periodLength;
  const periodCount = Number(scoring.periodCount);
  const gameLength = Number(scoring.gameLengthMinutes);
  if (Number.isFinite(gameLength) && gameLength > 0 && Number.isFinite(periodCount) && periodCount > 0) {
    return gameLength / periodCount;
  }
  if (Number.isFinite(gameLength) && gameLength > 0) return gameLength;
  return 20;
}

export function clockToGameSecond(period: number, minute: number, second: number, periodLengthMinutes: number): number {
  const safePeriod = Math.max(1, Number.isFinite(period) ? Math.floor(period) : 1);
  const periodLengthSeconds = Math.max(60, Math.floor(periodLengthMinutes * 60));
  const safeMinute = Math.max(0, Math.min(Math.floor(periodLengthSeconds / 60), Number.isFinite(minute) ? Math.floor(minute) : 0));
  const safeSecond = Math.max(0, Math.min(59, Number.isFinite(second) ? Math.floor(second) : 0));
  return (safePeriod - 1) * periodLengthSeconds + safeMinute * 60 + safeSecond;
}

export function gameSecondToClock(gameSecond: number | undefined, periodLengthMinutes: number): { minute: number; second: number } | undefined {
  if (gameSecond === undefined || !Number.isFinite(gameSecond)) return undefined;
  const periodLengthSeconds = Math.max(60, Math.floor(periodLengthMinutes * 60));
  const withinPeriod = Math.max(0, Math.floor(gameSecond)) % periodLengthSeconds;
  return {
    minute: Math.floor(withinPeriod / 60),
    second: withinPeriod % 60,
  };
}

export function tallyGameEvents(events: ReadonlyArray<ScoreEvent>, game: Pick<Game, 'homeTeamId' | 'awayTeamId'>): GameTally {
  const tally: GameTally = {
    homeScore: 0,
    awayScore: 0,
    homeShootoutWins: 0,
    awayShootoutWins: 0,
    homeShootoutGoals: 0,
    awayShootoutGoals: 0,
    shootoutAttempts: 0,
    homeShots: 0,
    awayShots: 0,
    homePims: 0,
    awayPims: 0,
    goalsByPlayer: {},
    assistsByPlayer: {},
    shotsByPlayer: {},
    pimsByPlayer: {},
  };
  for (const e of events) {
    if (e.type === 'goal' || e.type === 'penalty_shot_made') {
      if (e.teamId === game.homeTeamId) tally.homeScore += 1;
      else if (e.teamId === game.awayTeamId) tally.awayScore += 1;
      if (e.playerId) tally.goalsByPlayer[e.playerId] = (tally.goalsByPlayer[e.playerId] ?? 0) + 1;
    } else if (e.type === 'assist') {
      if (e.playerId) tally.assistsByPlayer[e.playerId] = (tally.assistsByPlayer[e.playerId] ?? 0) + 1;
    } else if (e.type === 'shootout_attempt') {
      tally.shootoutAttempts += 1;
      if (isShootoutMade(e)) {
        if (e.teamId === game.homeTeamId) {
          tally.homeShootoutWins += 1;
          tally.homeShootoutGoals += 1;
        } else if (e.teamId === game.awayTeamId) {
          tally.awayShootoutWins += 1;
          tally.awayShootoutGoals += 1;
        }
      }
    } else if (e.type === 'shot_on_goal') {
      if (e.teamId === game.homeTeamId) tally.homeShots += 1;
      else if (e.teamId === game.awayTeamId) tally.awayShots += 1;
      if (e.playerId) tally.shotsByPlayer[e.playerId] = (tally.shotsByPlayer[e.playerId] ?? 0) + 1;
    } else if (e.type === 'penalty') {
      const pim = e.penaltyMinutes ?? minutesForClass(e.penaltyClass);
      if (e.teamId === game.homeTeamId) tally.homePims += pim;
      else if (e.teamId === game.awayTeamId) tally.awayPims += pim;
      if (e.playerId) tally.pimsByPlayer[e.playerId] = (tally.pimsByPlayer[e.playerId] ?? 0) + pim;
    }
  }
  return tally;
}

/** Default penalty minutes per classification. Used when an event omits an explicit duration. */
function minutesForClass(c?: import('../models').PenaltyClassification): number {
  switch (c) {
    case 'major': return 5;
    case 'misconduct': return 10;
    case 'game_misconduct': return 10;
    case 'match': return 25;
    case 'bench_minor':
    case 'minor':
    default: return 2;
  }
}

export interface StandingsInput {
  tournamentId: Id;
  divisions: ReadonlyArray<Division>;
  teams: ReadonlyArray<Team>;
  games: ReadonlyArray<Game>;
  results: ReadonlyArray<GameResult>;
  events?: ReadonlyArray<ScoreEvent>;
  settings: TournamentSettings;
}

export function isShootoutMade(event: Pick<ScoreEvent, 'shootoutMade' | 'notes'>): boolean {
  if (typeof event.shootoutMade === 'boolean') return event.shootoutMade;
  return event.notes === 'made';
}

export function computeStandings(input: StandingsInput): StandingsRecord[] {
  const { tournamentId, divisions, teams, games, results, settings } = input;
  const resultByGameId = new Map(results.map((r) => [r.gameId, r]));
  const eventsByGameId = new Map<Id, ScoreEvent[]>();
  for (const event of input.events ?? []) {
    const list = eventsByGameId.get(event.gameId) ?? [];
    list.push(event);
    eventsByGameId.set(event.gameId, list);
  }
  const records: StandingsRecord[] = [];

  for (const div of divisions) {
    const divTeams = teams.filter((t) => t.divisionId === div.id);
    const base = new Map<Id, StandingsRecord>();
    for (const team of divTeams) {
      base.set(team.id, {
        id: `standings-${team.id}`,
        kind: 'standings-record',
        createdAt: team.createdAt,
        updatedAt: team.updatedAt,
        tournamentId,
        divisionId: div.id,
        teamId: team.id,
        gamesPlayed: 0,
        wins: 0,
        losses: 0,
        ties: 0,
        otLosses: 0,
        goalsFor: 0,
        goalsAgainst: 0,
        shootoutWins: 0,
        regulationWins: 0,
        overtimeWins: 0,
        shootoutLosses: 0,
        penaltyMinutes: 0,
        points: 0,
      });
    }

    for (const game of games) {
      if (game.divisionId !== div.id) continue;
      const result = resultByGameId.get(game.id);
      if (!result) continue;
      const home = base.get(game.homeTeamId);
      const away = base.get(game.awayTeamId);
      if (!home || !away) continue;
      home.gamesPlayed += 1;
      away.gamesPlayed += 1;
      home.goalsFor += result.homeScore;
      home.goalsAgainst += result.awayScore;
      away.goalsFor += result.awayScore;
      away.goalsAgainst += result.homeScore;
      const tally = tallyGameEvents(eventsByGameId.get(game.id) ?? [], game);
      home.penaltyMinutes = (home.penaltyMinutes ?? 0) + tally.homePims;
      away.penaltyMinutes = (away.penaltyMinutes ?? 0) + tally.awayPims;
      if (result.homeScore > result.awayScore) {
        home.wins += 1;
        if (result.decidedBy === 'overtime') {
          home.overtimeWins = (home.overtimeWins ?? 0) + 1;
          away.otLosses = (away.otLosses ?? 0) + 1;
        } else if (result.decidedBy === 'shootout') {
          home.shootoutWins += 1;
          away.shootoutLosses = (away.shootoutLosses ?? 0) + 1;
          away.otLosses = (away.otLosses ?? 0) + 1;
        } else {
          home.regulationWins = (home.regulationWins ?? 0) + 1;
        }
        away.losses += 1;
      } else if (result.awayScore > result.homeScore) {
        away.wins += 1;
        if (result.decidedBy === 'overtime') {
          away.overtimeWins = (away.overtimeWins ?? 0) + 1;
          home.otLosses = (home.otLosses ?? 0) + 1;
        } else if (result.decidedBy === 'shootout') {
          away.shootoutWins += 1;
          home.shootoutLosses = (home.shootoutLosses ?? 0) + 1;
          home.otLosses = (home.otLosses ?? 0) + 1;
        } else {
          away.regulationWins = (away.regulationWins ?? 0) + 1;
        }
        home.losses += 1;
      } else {
        home.ties += 1;
        away.ties += 1;
      }
    }

    base.forEach((rec) => {
      const shootoutLosses = rec.shootoutLosses ?? 0;
      const overtimeOnlyLosses = Math.max(0, (rec.otLosses ?? 0) - shootoutLosses);
      rec.points =
        (rec.regulationWins ?? 0) * (settings.scoring.pointsRegulationWin ?? 2) +
        (rec.overtimeWins ?? 0) * (settings.scoring.pointsOvertimeWin ?? settings.scoring.pointsRegulationWin ?? 2) +
        rec.shootoutWins * (settings.scoring.pointsShootoutWin ?? settings.scoring.pointsRegulationWin ?? 2) +
        overtimeOnlyLosses * (settings.scoring.pointsOvertimeLoss ?? 1) +
        shootoutLosses * (settings.scoring.pointsShootoutLoss ?? settings.scoring.pointsOvertimeLoss ?? 1) +
        rec.ties * (settings.scoring.pointsTie ?? 1);
    });

    const sorted = Array.from(base.values()).sort((a, b) =>
      tieBreakerCompare(a, b, settings.scoring.tiebreakers, games, resultByGameId),
    );
    sorted.forEach((rec, i) => {
      rec.rank = i + 1;
    });
    records.push(...sorted);
  }
  return records;
}

function tieBreakerCompare(
  a: StandingsRecord,
  b: StandingsRecord,
  chain: TournamentSettings['scoring']['tiebreakers'],
  games: ReadonlyArray<Game>,
  results: Map<Id, GameResult>,
): number {
  // Default: points desc, then chain.
  const pointsCmp = (b.points ?? 0) - (a.points ?? 0);
  if (pointsCmp !== 0) return pointsCmp;
  for (const tb of chain) {
    const v = compareByTiebreaker(a, b, tb, games, results);
    if (v !== 0) return v;
  }
  return a.teamId.localeCompare(b.teamId);
}

function compareByTiebreaker(
  a: StandingsRecord,
  b: StandingsRecord,
  tb: TournamentSettings['scoring']['tiebreakers'][number],
  games: ReadonlyArray<Game>,
  results: Map<Id, GameResult>,
): number {
  switch (tb) {
    case 'wins':
      return b.wins - a.wins;
    case 'goal_diff':
      return (b.goalsFor - b.goalsAgainst) - (a.goalsFor - a.goalsAgainst);
    case 'goals_for':
      return b.goalsFor - a.goalsFor;
    case 'shootout_wins':
      return b.shootoutWins - a.shootoutWins;
    case 'fewest_pims':
      return (a.penaltyMinutes ?? 0) - (b.penaltyMinutes ?? 0);
    case 'head_to_head': {
      // Look at games between these two teams only.
      let aPts = 0;
      let bPts = 0;
      for (const g of games) {
        const r = results.get(g.id);
        if (!r) continue;
        const isAB = g.homeTeamId === a.teamId && g.awayTeamId === b.teamId;
        const isBA = g.homeTeamId === b.teamId && g.awayTeamId === a.teamId;
        if (!isAB && !isBA) continue;
        const aHome = g.homeTeamId === a.teamId;
        const aScore = aHome ? r.homeScore : r.awayScore;
        const bScore = aHome ? r.awayScore : r.homeScore;
        if (aScore > bScore) aPts += 2;
        else if (bScore > aScore) bPts += 2;
        else {
          aPts += 1;
          bPts += 1;
        }
      }
      return bPts - aPts;
    }
    default:
      return 0;
  }
}

export interface PlayerStat {
  playerId: Id;
  teamId: Id;
  gamesPlayed: number;
  goals: number;
  assists: number;
  points: number;
  shots: number;
  penaltyMinutes: number;
  gameWinningGoals: number;
  powerPlayGoals: number;
  shortHandedGoals: number;
  shootoutAttempts: number;
  shootoutGoals: number;
  shootoutPct: number;
}

export function computePlayerStats(input: {
  tournamentId: Id;
  games: ReadonlyArray<Game>;
  events: ReadonlyArray<ScoreEvent>;
  rosterEntries: ReadonlyArray<{ teamId: Id; playerId: Id }>;
  gameRosters?: ReadonlyArray<Pick<GameRoster, 'gameId' | 'teamId' | 'attendanceByPlayerId'>>;
}): PlayerStat[] {
  const stats = new Map<Id, PlayerStat>();
  // Init from roster.
  for (const r of input.rosterEntries) {
    if (!stats.has(r.playerId)) {
      stats.set(r.playerId, {
        playerId: r.playerId,
        teamId: r.teamId,
        gamesPlayed: 0,
        goals: 0,
        assists: 0,
        points: 0,
        shots: 0,
        penaltyMinutes: 0,
        gameWinningGoals: 0,
        powerPlayGoals: 0,
        shortHandedGoals: 0,
        shootoutAttempts: 0,
        shootoutGoals: 0,
        shootoutPct: 0,
      });
    }
  }
  for (const e of input.events) {
    if (!e.playerId) continue;
    const s =
      stats.get(e.playerId) ??
      ({
        playerId: e.playerId,
        teamId: e.teamId ?? '',
        gamesPlayed: 0,
        goals: 0,
        assists: 0,
        points: 0,
        shots: 0,
        penaltyMinutes: 0,
        gameWinningGoals: 0,
        powerPlayGoals: 0,
        shortHandedGoals: 0,
        shootoutAttempts: 0,
        shootoutGoals: 0,
        shootoutPct: 0,
      } as PlayerStat);
    if (e.type === 'goal' || e.type === 'penalty_shot_made') {
      s.goals += 1;
      if (e.isGameWinner) s.gameWinningGoals += 1;
      if (e.isPowerPlay) s.powerPlayGoals += 1;
      if (e.isShortHanded) s.shortHandedGoals += 1;
    }
    if (e.type === 'assist') s.assists += 1;
    if (e.type === 'shot_on_goal') s.shots += 1;
    if (e.type === 'penalty') {
      s.penaltyMinutes += e.penaltyMinutes ?? 2;
    }
    if (e.type === 'shootout_attempt') {
      s.shootoutAttempts += 1;
      if (isShootoutMade(e)) s.shootoutGoals += 1;
    }
    s.points = s.goals + s.assists;
    s.shootoutPct = s.shootoutAttempts > 0 ? s.shootoutGoals / s.shootoutAttempts : 0;
    stats.set(e.playerId, s);
  }
  const rosterTeamsByPlayer = new Map<Id, Set<Id>>();
  for (const roster of input.rosterEntries) {
    const set = rosterTeamsByPlayer.get(roster.playerId) ?? new Set<Id>();
    set.add(roster.teamId);
    rosterTeamsByPlayer.set(roster.playerId, set);
  }
  const gameRosterByGameTeam = new Map<string, Pick<GameRoster, 'gameId' | 'teamId' | 'attendanceByPlayerId'>>();
  for (const gameRoster of input.gameRosters ?? []) {
    gameRosterByGameTeam.set(`${gameRoster.gameId}:${gameRoster.teamId}`, gameRoster);
  }
  const finalGames = input.games.filter((game) => game.status === 'final' || game.status === 'forfeit');
  const played = new Map<Id, Set<Id>>();
  for (const [playerId, teamIds] of rosterTeamsByPlayer.entries()) {
    for (const game of finalGames) {
      const playerTeamId = [game.homeTeamId, game.awayTeamId].find((teamId) => teamIds.has(teamId));
      if (!playerTeamId) continue;
      const gameRoster = gameRosterByGameTeam.get(`${game.id}:${playerTeamId}`);
      const attendance = gameRoster?.attendanceByPlayerId ?? {};
      const attendanceCaptured = Object.keys(attendance).length > 0;
      if (attendanceCaptured && attendance[playerId] === 'absent') continue;
      const set = played.get(playerId) ?? new Set<Id>();
      set.add(game.id);
      played.set(playerId, set);
    }
  }
  for (const [pid, gset] of played.entries()) {
    const s = stats.get(pid);
    if (s) s.gamesPlayed = gset.size;
  }
  return Array.from(stats.values()).sort((a, b) => b.points - a.points || b.goals - a.goals);
}

export interface BracketSeed {
  divisionId: Id;
  seed: number;
  teamId: Id;
}

export function computeBracketSeeds(standings: ReadonlyArray<StandingsRecord>): BracketSeed[] {
  const out: BracketSeed[] = [];
  const byDiv = new Map<Id, StandingsRecord[]>();
  for (const s of standings) {
    const list = byDiv.get(s.divisionId) ?? [];
    list.push(s);
    byDiv.set(s.divisionId, list);
  }
  for (const [divisionId, list] of byDiv.entries()) {
    list.sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999));
    list.forEach((rec, i) => {
      out.push({ divisionId, seed: i + 1, teamId: rec.teamId });
    });
  }
  return out;
}

/**
 * Compute aggregated goalie stats from score events + game results.
 *
 * A goalie is "credited" with a game when a `goalie_change` marker assigns
 * them, OR when goal/save/SOG events carry their `goalieId`. Falls back to
 * each team's primary goalie (first roster entry with position 'G' / 'goalie').
 */
export function computeGoalieStats(input: {
  games: ReadonlyArray<Game>;
  events: ReadonlyArray<ScoreEvent>;
  results: ReadonlyArray<GameResult>;
  rosterEntries: ReadonlyArray<{ teamId: Id; playerId: Id; position?: string }>;
}): GoalieStat[] {
  const { games, events, results, rosterEntries } = input;
  const goalieByTeam = new Map<Id, Id>();
  for (const r of rosterEntries) {
    if (!r.position) continue;
    const p = r.position.toLowerCase();
    if ((p === 'g' || p === 'goalie') && !goalieByTeam.has(r.teamId)) {
      goalieByTeam.set(r.teamId, r.playerId);
    }
  }
  const eventsByGame = new Map<Id, ScoreEvent[]>();
  for (const e of events) {
    const list = eventsByGame.get(e.gameId) ?? [];
    list.push(e);
    eventsByGame.set(e.gameId, list);
  }
  const stats = new Map<Id, GoalieStat>();
  const ensure = (playerId: Id, teamId: Id): GoalieStat => {
    let s = stats.get(playerId);
    if (!s) {
      s = {
        playerId, teamId,
        gamesPlayed: 0, saves: 0, goalsAgainst: 0, shotsAgainst: 0,
        wins: 0, losses: 0, shutouts: 0, savePct: 0, gaa: 0,
        shootoutShotsAgainst: 0, shootoutSaves: 0, shootoutGoalsAgainst: 0, shootoutSavePct: 0,
      };
      stats.set(playerId, s);
    }
    return s;
  };
  for (const game of games) {
    const homeG = goalieByTeam.get(game.homeTeamId);
    const awayG = goalieByTeam.get(game.awayTeamId);
    const result = results.find((r) => r.gameId === game.id);
    const ev = eventsByGame.get(game.id) ?? [];
    if (homeG) {
      const s = ensure(homeG, game.homeTeamId);
      s.gamesPlayed += 1;
      for (const e of ev) {
        if (e.teamId !== game.awayTeamId) continue;
        if (e.type === 'shot_on_goal') s.shotsAgainst += 1;
        if (e.type === 'goal' && !e.isEmptyNet) {
          s.goalsAgainst += 1;
          s.shotsAgainst += 1; // every goal is a shot
        }
        if (e.type === 'save') s.saves += 1;
        if (e.type === 'shootout_attempt' && e.goalieId === homeG) {
          s.shootoutShotsAgainst = (s.shootoutShotsAgainst ?? 0) + 1;
          if (isShootoutMade(e)) s.shootoutGoalsAgainst = (s.shootoutGoalsAgainst ?? 0) + 1;
          else s.shootoutSaves = (s.shootoutSaves ?? 0) + 1;
        }
      }
      // Implicit saves: SOG against minus goals against (when no explicit save events were tracked).
      const hasExplicitSaves = ev.some((e) => e.type === 'save');
      if (!hasExplicitSaves) s.saves = Math.max(0, s.shotsAgainst - s.goalsAgainst);
      if (result) {
        if (result.winningTeamId === game.homeTeamId) s.wins += 1;
        else if (result.winningTeamId === game.awayTeamId) s.losses += 1;
        if (result.awayScore === 0) s.shutouts += 1;
      }
    }
    if (awayG) {
      const s = ensure(awayG, game.awayTeamId);
      s.gamesPlayed += 1;
      for (const e of ev) {
        if (e.teamId !== game.homeTeamId) continue;
        if (e.type === 'shot_on_goal') s.shotsAgainst += 1;
        if (e.type === 'goal' && !e.isEmptyNet) {
          s.goalsAgainst += 1;
          s.shotsAgainst += 1;
        }
        if (e.type === 'save') s.saves += 1;
        if (e.type === 'shootout_attempt' && e.goalieId === awayG) {
          s.shootoutShotsAgainst = (s.shootoutShotsAgainst ?? 0) + 1;
          if (isShootoutMade(e)) s.shootoutGoalsAgainst = (s.shootoutGoalsAgainst ?? 0) + 1;
          else s.shootoutSaves = (s.shootoutSaves ?? 0) + 1;
        }
      }
      const hasExplicitSaves = ev.some((e) => e.type === 'save');
      if (!hasExplicitSaves) s.saves = Math.max(0, s.shotsAgainst - s.goalsAgainst);
      if (result) {
        if (result.winningTeamId === game.awayTeamId) s.wins += 1;
        else if (result.winningTeamId === game.homeTeamId) s.losses += 1;
        if (result.homeScore === 0) s.shutouts += 1;
      }
    }
  }
  for (const s of stats.values()) {
    s.savePct = s.shotsAgainst > 0 ? s.saves / s.shotsAgainst : 0;
    s.shootoutSavePct = (s.shootoutShotsAgainst ?? 0) > 0
      ? (s.shootoutSaves ?? 0) / (s.shootoutShotsAgainst ?? 1)
      : 0;
    s.gaa = s.gamesPlayed > 0 ? s.goalsAgainst / s.gamesPlayed : 0;
  }
  return Array.from(stats.values()).sort((a, b) => b.savePct - a.savePct);
}

/**
 * Identify the game-winning goal in an ordered event list.
 *
 * Defined as the goal that put the winner ahead to stay (the (loser+1)-th
 * goal scored by the winning team). Returns the event id, or undefined if
 * the game was tied / forfeited / decided in shootout.
 */
export function findGameWinnerEventId(
  events: ReadonlyArray<ScoreEvent>,
  game: Pick<Game, 'homeTeamId' | 'awayTeamId'>,
  result: Pick<GameResult, 'homeScore' | 'awayScore' | 'winningTeamId' | 'decidedBy'>,
): Id | undefined {
  if (result.decidedBy === 'shootout' || result.decidedBy === 'forfeit') return undefined;
  if (result.homeScore === result.awayScore) return undefined;
  const winnerId = result.winningTeamId;
  if (!winnerId) return undefined;
  const loserScore = winnerId === game.homeTeamId ? result.awayScore : result.homeScore;
  let winnerGoals = 0;
  const ordered = [...events].sort((a, b) => a.recordedAt.localeCompare(b.recordedAt));
  for (const e of ordered) {
    const isGoal = e.type === 'goal' || e.type === 'penalty_shot_made';
    if (!isGoal) continue;
    if (e.teamId !== winnerId) continue;
    winnerGoals += 1;
    if (winnerGoals === loserScore + 1) return e.id;
  }
  return undefined;
}

